import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  selectedTime: string | null;
  onDateSelect: (date: number) => void;
  onTimeSelect: (time: string) => void;
  onMonthChange: (increment: number) => void;
}

export const CalendarModal: React.FC<CalendarModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  selectedTime,
  onDateSelect,
  onTimeSelect,
  onMonthChange,
}) => {
  const formatDate = (date: Date) => {
    return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, "0")}월`;
  };
  const { t } = useTranslation();
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getMonthStart = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedDate);
    const monthStart = getMonthStart(selectedDate);
    const days = [];

    for (let i = 0; i < monthStart; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const isSelected = i === selectedDate.getDate();
      const isToday =
        new Date().toDateString() ===
        new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          i,
        ).toDateString();
      days.push(
        <button
          key={i}
          onClick={() => onDateSelect(i)}
          className={`flex h-8 w-8 items-center justify-center rounded-full p-[0.5rem] text-sm ${isSelected ? "bg-[#A8C6FF] text-white" : ""} ${isToday && !isSelected ? "text-[#A8C6FF]" : ""} ${!isSelected && !isToday ? "hover:bg-gray-100" : ""} `}
        >
          {i}
        </button>,
      );
    }

    return days;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative h-[27.375rem] w-[43.125rem] rounded-2xl bg-white p-[1.5rem] shadow-xl">
        <div className="flex justify-between gap-[2rem]">
          <div className="w-[19rem]">
            <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => onMonthChange(-1)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="text-base font-semibold">
                  {formatDate(selectedDate)}
                </div>
                <button
                  onClick={() => onMonthChange(1)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {["일", "월", "화", "수", "목", "금", "토"].map((day) => (
                  <div key={day} className="p-[0.5rem] text-sm font-medium">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
            </div>
          </div>
          <div className="w-[19.625rem]">
            <h3 className="decoration-skip-none mb-4 text-left font-sans text-sm font-normal leading-[1.375rem] text-[#5E6D77] underline-offset-auto">
              {t("Select time")}
            </h3>
            <div className="grid grid-cols-3 gap-[1rem]">
              {[
                "08:00 AM",
                "09:00 AM",
                "10:00 AM",
                "11:00 AM",
                "12:00 PM",
                "01:00 PM",
                "02:00 PM",
                "03:00 PM",
                "04:00 PM",
                "05:00 PM",
                "06:00 PM",
                "07:00 PM",
              ].map((time) => (
                <button
                  key={time}
                  onClick={() => onTimeSelect(time)}
                  className={`h-[2.5rem] w-[5.875rem] rounded-lg border border-[#000000] text-sm ${
                    selectedTime === time
                      ? "border-[#007AFF] bg-[#DBE7FF] text-[#007AFF]"
                      : "bg-white hover:bg-[#DBE7FF]"
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-[1.5rem] right-[1.5rem]">
          <button
            onClick={onClose}
            className="w-[7.4375rem] rounded-full bg-[#A8C6FF] px-6 py-3 text-base font-medium text-[#232323]"
          >
            {t("Apply")}
          </button>
        </div>
      </div>
    </div>
  );
};
